/**
 * SQLite file-path / URL parser → sqlite.
 *
 *   /Users/me/data/app.db
 *   C:\data\app.sqlite
 *   file:///Users/me/data/app.db
 *   sqlite:///Users/me/data/app.db
 *
 * Note: the connection form stores the SQLite file path in `server` (see
 * NewConnectionPopup.buildConnection), so we set `server` here too.
 */

import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import type { ConnectionStringParser } from './types';
import { safeDecode } from './helpers';

const SQLITE_EXT_RE = /\.(db|sqlite|sqlite3)$/i;
const ABSOLUTE_PATH_RE = /^(\/|[a-zA-Z]:[\\/])/;

export const parseSqlitePath: ConnectionStringParser = (input) => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const uriMatch = /^(sqlite|file):\/\/(.*)$/i.exec(trimmed);
  let path: string;
  if (uriMatch) {
    path = safeDecode(uriMatch[2]);
    // Windows drive letter in a file URI: /C:/Users → C:/Users
    if (/^\/[a-zA-Z]:/.test(path)) path = path.slice(1);
  } else {
    // Bare path: must be absolute and end in a SQLite extension.
    if (!ABSOLUTE_PATH_RE.test(trimmed) || !SQLITE_EXT_RE.test(trimmed)) {
      return null;
    }
    path = trimmed;
  }

  if (!path) return null;

  const values: Partial<ConnectionFormValues> = {
    engine: 'sqlite',
    server: path,
    port: '0',
    authentication: 'No password',
  };

  return {
    format: 'sqlite_path',
    engine: 'sqlite',
    values,
    matchedFields: ['database file'],
    unmatchedKeys: [],
  };
};
